import React from 'react'
import PropTypes from 'prop-types'
import { compose, withApollo } from 'react-apollo'
import { message, Avatar, Button, Row, Form, Input } from 'antd'
import { withFormik } from 'formik'
import * as yup from 'yup'
import { Wrapper, Logo, RecaptchaHint, RecaptchaStatus } from './styles'
import Version from '../../components/Version'
import API from '../../api/Http'
import * as EndPoints from '../../api/EndPoints'
import { loadRecaptcha, executeRecaptcha } from './recaptcha'

const logo = require('../../assets/images/g2g.png')

const FormItem = Form.Item
const RECAPTCHA_ACTION = 'admin_login'

const validationSchema = () => {
  /* istanbul ignore next */
  return yup.object().shape({
    email: yup
      .string()
      .email()
      .required(),
    password: yup.string().required()
  })
}

export const handleSubmit = async (values, { props, setSubmitting }) => {
  const { email, password } = values
  const { client, getRecaptchaToken } = props

  let result

  try {
    const recaptchaToken = await getRecaptchaToken()
    const data = {
      email: email,
      password: password,
      recaptchaToken: recaptchaToken
    }
    result = await API.post(EndPoints.USER + `/login`, data)

    if (result.status === 200) {
      localStorage.setItem('user-token', result.data.data.token)
      localStorage.setItem('user-name', result.data.data.name)
      localStorage.setItem('data', JSON.stringify(result.data.data));
      localStorage.setItem('expire', result.data.data.expire)
      client.resetStore()
      window.location.href = '/'
    }
  } catch (error) {
    const responseMessage =
      error &&
      error.response &&
      error.response.data &&
      (error.response.data.message || error.response.data.error)

    message.error(
      responseMessage ||
        (error && error.name === 'RecaptchaError'
          ? error.message
          : 'ชื่อผู้ใช้งานหรือรหัสผ่านไม่ถูกต้อง')
    )
    console.log(error)
  } finally {
    setSubmitting(false)
  }
}

export const LoginForm = ({
  handleSubmit,
  isSubmitting,
  isValid,
  dirty,
  handleChange,
  handleBlur,
  errors,
  touched,
  recaptchaStatus,
  recaptchaMessage
}) => {
  const isSubmitDisabled =
    !dirty || !isValid || isSubmitting || recaptchaStatus !== 'ready'

  return (
    <React.Fragment>
      <Wrapper>
        <Logo>
          <Avatar src={logo} size={64} />
        </Logo>
        <form onSubmit={handleSubmit}>
          <FormItem
            hasFeedback
            help={touched.email && errors.email}
            validateStatus={touched.email && errors.email && 'error'}
          >
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="Email"
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </FormItem>
          <FormItem
            help={touched.password && errors.password}
            validateStatus={touched.password && errors.password && 'error'}
          >
            <Input.Password
              id="password"
              name="password"
              type="password"
              placeholder="Password"
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </FormItem>
          <RecaptchaStatus error={recaptchaStatus === 'error'}>
            {recaptchaStatus === 'loading' && 'Preparing security check...'}
            {recaptchaStatus === 'error' && recaptchaMessage}
          </RecaptchaStatus>
          <Row>
            <Button
              htmlType="submit"
              type="primary"
              loading={isSubmitting}
              disabled={isSubmitDisabled}>
              Sign In
            </Button>
          </Row>
          <RecaptchaHint>
            This site is protected by reCAPTCHA and the Google{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>{' '}
            apply.
          </RecaptchaHint>
        </form>
      </Wrapper>
      <Version />
    </React.Fragment>
  )
}

LoginForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isValid: PropTypes.bool.isRequired,
  dirty: PropTypes.bool.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  recaptchaStatus: PropTypes.string.isRequired,
  recaptchaMessage: PropTypes.string
}

LoginForm.defaultProps = {
  recaptchaMessage: ''
}

const LoginWithFormik = compose(
  withApollo,
  withFormik({
    handleSubmit,
    validationSchema,
    displayName: 'LoginForm'
  })
)(LoginForm)

export default function Login(props) {
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY
  const [recaptchaStatus, setRecaptchaStatus] = React.useState(
    siteKey ? 'loading' : 'error'
  )
  const [recaptchaMessage, setRecaptchaMessage] = React.useState(
    siteKey ? '' : 'Security check is not configured for this environment.'
  )

  React.useEffect(() => {
    let isMounted = true

    if (!siteKey) {
      setRecaptchaStatus('error')
      setRecaptchaMessage('Security check is not configured for this environment.')
      return undefined
    }

    setRecaptchaStatus('loading')
    setRecaptchaMessage('')

    loadRecaptcha(siteKey)
      .then(() => {
        if (!isMounted) {
          return
        }

        setRecaptchaStatus('ready')
      })
      .catch((error) => {
        if (!isMounted) {
          return
        }

        setRecaptchaStatus('error')
        setRecaptchaMessage(
          error && error.message
            ? error.message
            : 'Unable to load reCAPTCHA. Please refresh the page.'
        )
      })

    return () => {
      isMounted = false
    }
  }, [siteKey])

  const getRecaptchaToken = () => {
    return executeRecaptcha(siteKey, RECAPTCHA_ACTION)
  }

  return (
    <LoginWithFormik
      {...props}
      getRecaptchaToken={getRecaptchaToken}
      recaptchaStatus={recaptchaStatus}
      recaptchaMessage={recaptchaMessage}
    />
  )
}
